import { useState, useCallback } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { getPricePattern } from '../services/Helpers';
import { useTranslation } from 'react-i18next';
import useCurrency from '../hooks/useCurrency';
import ImageDrop from '../ImageDrop/ImageDrop';
import { Image } from 'react-bootstrap-icons';
import styles from '../Shop.module.css';

export default function ProductEditor({
    shop,
    product,
    onChange,
    onImageDelete,
    onImageFileChange,
} = {}) {
    const { t } = useTranslation();
    const currency = useCurrency(shop);
    const [isValidTitle, setIsValidTitle] = useState(true);
    const [isValidPrice, setIsValidPrice] = useState(true);

    const isAttrChanged = useCallback((name, value) => {
        if (name === 'price') {
            return Number(product.variants[0].price) !== Number(value);
        }
        return product[name] !== value;
    }, [product]);

    const onAttrChange = useCallback((e) => {
        if (!(onChange && e.target.validity.valid)) {
            return;
        }
        const { name, type, value, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        if (isAttrChanged(name, newValue)) {
            onChange({ [name]: newValue });
        }
    }, [onChange, isAttrChanged]);

    const onKeyPress = useCallback((e) => {
        if (e.code === 'Enter') {
            onAttrChange(e);
        }
    }, [onAttrChange]);

    return <>
        <Form.Group className="mb-3 col-12">
            <div className={`${styles.listItem} w-100 mb-2 d-flex bg-light fw-bolder align-items-center rounded`}>
                {
                    product.images.length
                        ? <img className={styles.Image} width={60} height={60} src={product.images[0].src} />
                        : <div className={`${styles.NoImage} d-flex align-items-center justify-content-center`}>
                            <small className={`${styles.NoImageText} text-center bg-white rounded`}>
                                <Image className="text-secondary" size={24} />
                            </small>
                          </div>
                }
                <div className="flex-fill p-3 m-0">
                    <h5 className="text-center m-0 fw-normal">{product.title}</h5>
                </div>
            </div>
        </Form.Group>
        <Form.Group className="mb-3 col-12">
            <Form.Label className={isValidTitle ? '' : styles.isInvalidValue}>{t('Title')}</Form.Label>
            <Form.Control
                type="text"
                name="title"
                onBlur={onAttrChange}
                onChange={(e) => setIsValidTitle(e.target.validity.valid)}
                isInvalid={!isValidTitle}
                onKeyPress={onKeyPress}
                defaultValue={product.title}
                required
            />
        </Form.Group>
        <Form.Group className="mb-3 col-12">
            <Form.Label className={isValidPrice ? '' : styles.isInvalidValue}>{t('Price')}</Form.Label>
            <InputGroup>
                <InputGroup.Text>{currency.symbol}</InputGroup.Text>
                <Form.Control
                    type="text"
                    name="price"
                    onBlur={onAttrChange}
                    pattern={getPricePattern(currency.decimalDigits)}
                    onChange={(e) => setIsValidPrice(e.target.validity.valid)}
                    isInvalid={!isValidPrice}
                    onKeyPress={onKeyPress}
                    defaultValue={product.variants[0].price}
                    required
                />
            </InputGroup>
        </Form.Group>
        <Form.Group className="mb-3 col-12">
            <Form.Label>{t('Status')}</Form.Label>
            <Form.Select name="status" defaultValue={product.status} onChange={onAttrChange}
            isValid={product.status === 'ACTIVE'} isInvalid={product.status === 'DRAFT' || product.status === 'ARCHIVED'}>
                <option value="ACTIVE">Active</option>
                <option value="DRAFT">Draft</option>
                <option value="ARCHIVED">Archived</option>
            </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3 col-12">
            <Form.Label>{t('Description')}</Form.Label>
            <Form.Control
                as="textarea"
                rows={7}
                name="description"
                onBlur={onAttrChange}
                onKeyPress={onKeyPress}
                defaultValue={product.description}
            />
        </Form.Group>
        <Form.Group className="mb-3 col-12">
            <Form.Label>{t('Image')}</Form.Label>
            <ImageDrop
                images={product.images}
                className={`${styles.listItem} w-100 bg-light rounded d-flex justify-content-center align-items-center text-black-50 ${styles.dashed} `}
                onFileSelect={onImageFileChange}
                onImageDelete={onImageDelete}
            />
        </Form.Group>
    </>
}